import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetHouseholdAssetsForCrossEntityDp_BucketFragment,
  GetHouseholdAssetsForCrossEntityDp_GrantorFragment,
  useGetHouseholdAssetsForCrossEntityDpQuery,
} from '../graphql/GetHouseholdAssetsForCrossEntityDp.generated';
import {
  groupAssetStaticOptions,
  mapAssetGroupToOption,
  mapAssetStaticToOption,
  mapPolicyToOption,
  mapStakeToOption,
} from './useEntityAssetOptions';

/**
 * A group of asset options that all belong to a single source, used by the
 * cross-entity asset DP flow to group the "View all assets to distribute"
 * side panel by the entity (or grantor, for individually-held stakes)
 * currently holding each asset.
 */
export interface EntityAssetOptionGroup {
  /** Entity ID or ClientProfile ID of the holder; ID spaces are disjoint. */
  entityId: string;
  entityName: string;
  options: TypeaheadSelectInputOption<string>[];
}

interface UseHouseholdAssetsForCrossEntityDpResult {
  /** Asset options grouped by the entity currently holding each asset. */
  groups: EntityAssetOptionGroup[];
  /** Flat list of every option across all entities. */
  allOptions: TypeaheadSelectInputOption<string>[];
  loading: boolean;
}

/**
 * Build the asset options for a single entity's holdings bucket. Uses the
 * same per-asset mappers as {@link useEntityAssetOptions} so option shape
 * (display, value, groupName, endLabel) stays consistent between the inline
 * dropdown and the household-wide pool. Sort by dollar value descending so
 * the most valuable assets surface at the top of each entity's group in the
 * side drawer.
 */
function buildBucketOptions(
  bucket: GetHouseholdAssetsForCrossEntityDp_BucketFragment,
  groupByIntegratedAccount: boolean
): TypeaheadSelectInputOption<string>[] {
  // Track dollar value alongside each block of options so the list can be
  // sorted by value descending. Ownership stakes don't surface a $ value here,
  // so they fall to the bottom (sort 0). The asset block stays contiguous (so
  // integrated accounts keep their nested parent/child rows together) and sorts
  // by its most valuable holding.
  const withSort: Array<{
    sort: number;
    options: TypeaheadSelectInputOption<string>[];
  }> = [];

  const { entity, holdings } = bucket;

  // --- Businesses (ownership stakes) ---
  for (const stake of holdings.ownershipStakes) {
    withSort.push({ sort: 0, options: [mapStakeToOption(stake)] });
  }

  // --- Life insurance (policies held by the entity) ---
  for (const policy of holdings.insurancePolicies) {
    withSort.push({
      sort: new Decimal(policy.deathBenefitAmount).toNumber(),
      options: [mapPolicyToOption(policy)],
    });
  }

  // --- Assets from the entity's current valuation. When opted in, integrated
  //     accounts are folded into nested parent + holding rows (kept contiguous
  //     and sorted by their most valuable holding); otherwise each static is a
  //     flat option sorted on its own value. ---
  if (groupByIntegratedAccount) {
    const assetOptions = groupAssetStaticOptions(holdings.assets);
    if (assetOptions.length > 0) {
      const topAssetValue = holdings.assets.reduce((max, asset) => {
        const ownedValue = asset.currentValue?.ownedValue;
        return ownedValue != null
          ? Math.max(max, new Decimal(ownedValue).toNumber())
          : max;
      }, 0);
      withSort.push({ sort: topAssetValue, options: assetOptions });
    }
  } else {
    for (const asset of holdings.assets) {
      const ownedValue = asset.currentValue?.ownedValue;
      const sort = ownedValue != null ? new Decimal(ownedValue).toNumber() : 0;
      withSort.push({ sort, options: [mapAssetStaticToOption(asset)] });
    }
  }

  // Asset groups (not part of EntityHoldings; fetched alongside). In grouped
  // mode, integrated-account groups already appear as the nested parent rows
  // above, so they're skipped to avoid duplication; manual/non-integrated
  // groups stay listed so a cross-entity DP that targets one still resolves to
  // a chip and isn't dropped on save. In flat mode, every group is listed.
  for (const node of getNodes(entity.assetGroups)) {
    if (groupByIntegratedAccount && node.integrationKind != null) continue;
    withSort.push({ sort: 0, options: [mapAssetGroupToOption(node)] });
  }

  withSort.sort((a, b) => b.sort - a.sort);
  return withSort.flatMap((x) => x.options);
}

/**
 * Build the asset options for a single grantor. Grantors only contribute
 * their personally-held ownership stakes — asset groups, valuations, and
 * policies are modeled on entities, not on ClientProfile.
 */
function buildGrantorOptions(
  grantor: GetHouseholdAssetsForCrossEntityDp_GrantorFragment
): TypeaheadSelectInputOption<string>[] {
  return (grantor.ownedOwnershipStakes ?? []).map(mapStakeToOption);
}

/**
 * Fetches all assets across every entity in a household plus the
 * individually-held ownership stakes of each grantor, returning them as
 * per-source option groups. Used by the cross-entity asset DP flow at any
 * death event (e.g. side drawer for "View all assets to distribute" and
 * card-header display-name resolution for cross-entity picks).
 *
 * `excludeEntityId` filters the current entity out of the result. The side
 * panel builds the current entity's group from the inline dropdown options
 * instead, so that both pickers use the same option values.
 *
 * `skip` lets callers (e.g. non-asset DP recipient cards) avoid running
 * the household-wide query when the result wouldn't be consumed.
 *
 * `groupByIntegratedAccount` folds each integrated/linked account's holdings
 * into a nested parent + child rows (matching the inline picker); when off the
 * drawer keeps a flat list. Opt-in because only the asset-DP picker wires up
 * the parent cascade and group<->statics conversion.
 */
export function useHouseholdAssetsForCrossEntityDp({
  householdId,
  excludeEntityId,
  skip,
  groupByIntegratedAccount = false,
}: {
  householdId: string | null;
  excludeEntityId?: string | null;
  skip?: boolean;
  groupByIntegratedAccount?: boolean;
}): UseHouseholdAssetsForCrossEntityDpResult {
  const { data, loading } = useGetHouseholdAssetsForCrossEntityDpQuery({
    variables: { householdId: householdId ?? '' },
    skip: skip || !householdId,
    fetchPolicy: 'cache-and-network',
  });

  const groups = useMemo<EntityAssetOptionGroup[]>(() => {
    // Filter by householdId returns 0 or 1 household; flatMap keeps the read
    // safe even if the connection is empty.
    const household = getNodes(data?.households)[0];
    const buckets = household?.entityHoldings ?? [];
    // possiblePrimaryClients is the household's grantors (the same field
    // GetLifetimeExclusionAmounts and other DP-form queries read).
    const grantors = household?.possiblePrimaryClients ?? [];
    const result: EntityAssetOptionGroup[] = [];

    for (const bucket of buckets) {
      const { entity } = bucket;
      if (excludeEntityId && entity.id === excludeEntityId) continue;
      // Skip entities that are themselves held via an ownership stake (e.g.
      // an LLC owned by a client or trust). Their assets must not be listed
      // here because the DP source has to be the ownership stake, not the
      // assets nested under it — the backend has no way to resolve a nested
      // asset ID back to the holding entity.
      if ((entity.ownedByStakes ?? []).length > 0) continue;

      const options = buildBucketOptions(bucket, groupByIntegratedAccount);
      if (options.length === 0) continue;

      result.push({
        entityId: entity.id,
        entityName: entity.subtype.displayName,
        options,
      });
    }

    // Grantors contribute their individually-held ownership stakes as their
    // own group. The query already filters to grantors only, so non-grantor
    // ClientProfiles (children, beneficiaries) won't appear here. No
    // excludeEntityId check: the source entity for a DP is always an
    // entity, never a grantor — ID spaces are disjoint.
    for (const grantor of grantors) {
      const options = buildGrantorOptions(grantor);
      if (options.length === 0) continue;

      result.push({
        entityId: grantor.id,
        entityName: grantor.displayName,
        options,
      });
    }

    return result;
  }, [data, excludeEntityId, groupByIntegratedAccount]);

  const allOptions = useMemo(() => groups.flatMap((g) => g.options), [groups]);

  return { groups, allOptions, loading };
}
